import { useRef, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";

/**
 * Combined logo upload + URL paste field.
 * - Lets the editor either upload a logo file (PNG/JPG/SVG up to 5MB) or paste a remote URL.
 * - On successful upload, calls onChange with the public /api/logos/... URL.
 */
export default function LogoUploader({ label = "Logo", value, onChange, testId = "logo-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/logos/upload`, fd, { withCredentials: true });
      onChange(r.data.url);
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={value} alt="logo" className="h-14 w-14 object-contain bg-white/5 rounded border border-white/10" />
            <button type="button" onClick={() => onChange("")} className="absolute -top-1 -right-1 bg-aawta-jet rounded-full p-0.5 border border-white/20 text-white/60 hover:text-red-400" data-testid={`${testId}-clear`}>
              <X size={10} />
            </button>
          </div>
        ) : (
          <div className="h-14 w-14 flex items-center justify-center bg-white/5 border border-dashed border-white/15 text-white/30 text-[10px]">NO LOGO</div>
        )}
        <div className="flex-1 space-y-2">
          <input
            type="url" value={value || ""} placeholder="https://… (or upload)"
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testId}-url`}
            className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm"
          />
          <button
            type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid={`${testId}-upload-btn`}
            className="text-overline text-aawta-pink hover:text-white inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "UPLOADING…" : "UPLOAD FILE"}
          </button>
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" onChange={onPick} className="hidden" />
        </div>
      </div>
    </div>
  );
}
